// @flow

import produce from 'immer';
import type {
    ConsumerLoanSituationState,
    consumerLoanSituationActionType
} from './ConsumerLoanSituationTypes';

import {
    SET_CONSUMER_LOAN_SITUATION_STEP,
    SET_CONSUMER_LOAN_SITUATION_NEXT_STEP,
    SET_CONSUMER_LOAN_SITUATION_PREV_STEP,
    SET_CONSUMER_LOAN_SITUATION_TYPE,
    SET_CONSUMER_LOAN_SITUATION_CUSTOM_TYPE,
    SET_CONSUMER_LOAN_SITUATION_CREDIT_AMOUNT,
    SET_CONSUMER_LOAN_SITUATION_BORROWERS,
    SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_AGE,
    SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_AGE,
    SET_CONSUMER_LOAN_SITUATION_CHILDREN,
    SET_CONSUMER_LOAN_SITUATION_RESIDENCE,
    SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_REVENUE,
    SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_REVENUE,
    SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_EXPENSES,
    SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_EXPENSES,
    SET_CONSUMER_LOAN_SITUATION_SIMULATIONS, SET_CONSUMER_LOAN_SITUATION_SELECTED_SIMULATION,
} from '../../resources/constants/storeActions';
import { RESET_STORE } from '../../resources/constants/actionsTypes';

const initialState: ConsumerLoanSituationState = {
    currentStep: 0,
    previousStep: 0,
    lastStep: 0,
    type: '',
    customType: '',
    creditAmount: '',
    borrowers: 0,
    age: {
        mainBorrower: '',
        coBorrower: ''
    },
    children: -1,
    residence: null,
    revenues: {
        mainBorrower: {
            montlyIncome: 0,
            salaryPeriod: 12,
            familyAllowances: 0,
            pension: 0,
            rentalIncome: 0,
            otherIncome: 0
        },
        coBorrower: {
            montlyIncome: 0,
            salaryPeriod: 12,
            familyAllowances: 0,
            pension: 0,
            rentalIncome: 0,
            otherIncome: 0
        }
    },
    expenses: {
        mainBorrower: {
            rent: 0,
            loanPayments: 0,
            alimonyPayments: 0
        },
        coBorrower: {
            rent: 0,
            loanPayments: 0,
            alimonyPayments: 0
        }
    },
    simulations: [],
    selectedSimulation: null
};

const consumerLoanSituationReducer = (
    state: ConsumerLoanSituationState = initialState,
    action: consumerLoanSituationActionType,
) =>
    produce(state, (draft: ConsumerLoanSituationState) => {
        switch (action.type) {
            case SET_CONSUMER_LOAN_SITUATION_STEP:
                if (typeof action.payload === 'number') {
                    draft.previousStep = state.currentStep;
                    draft.currentStep = action.payload;
                    draft.lastStep = Math.max(draft.currentStep, state.lastStep);
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_NEXT_STEP:
                draft.previousStep = state.currentStep;
                draft.currentStep = state.currentStep + 1;
                draft.lastStep = Math.max(draft.currentStep, state.lastStep);
                break;

            case SET_CONSUMER_LOAN_SITUATION_PREV_STEP:
                if (state.currentStep > 0) {
                    draft.previousStep = state.currentStep;
                    draft.currentStep = state.currentStep - 1;
                }
                break;
    
            case SET_CONSUMER_LOAN_SITUATION_TYPE:
                if (typeof action.payload === 'string') {
                    draft.type = action.payload;
                }
                break;
  
            case SET_CONSUMER_LOAN_SITUATION_CUSTOM_TYPE:
                if (typeof action.payload === 'string') {
                    draft.customType = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_CREDIT_AMOUNT:
                if (typeof action.payload === 'number' || action.payload === '') {
                    draft.creditAmount = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_BORROWERS:
                if (typeof action.payload === 'number') {
                    draft.borrowers = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_AGE:
                if (typeof action.payload === 'number' || action.payload === '') {
                    draft.age.mainBorrower = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_AGE:
                if (typeof action.payload === 'number' || action.payload === '') {
                    draft.age.coBorrower = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_CHILDREN:
                if (typeof action.payload === 'number') {
                    draft.children = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_RESIDENCE:
                if (typeof action.payload === 'boolean' || action.payload === null) {
                    draft.residence = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_REVENUE:
                if (action.payload) {
                    draft.revenues = {
                        ...state.revenues,
                        mainBorrower: {
                            ...state.revenues.mainBorrower,
                            ...action.payload
                        }
                    };
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_REVENUE:
                if (action.payload) {
                    draft.revenues = {
                        ...state.revenues,
                        coBorrower: {
                            ...state.revenues.coBorrower,
                            ...action.payload
                        }
                    };
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_MAIN_BORROWER_EXPENSES:
                if (action.payload) {
                    draft.expenses = {
                        ...state.expenses,
                        mainBorrower: {
                            ...state.expenses.mainBorrower,
                            ...action.payload
                        }
                    };
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_CO_BORROWER_EXPENSES:
                if (action.payload) {
                    draft.expenses = {
                        ...state.expenses,
                        coBorrower: {
                            ...state.expenses.coBorrower,
                            ...action.payload
                        }
                    };
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_SIMULATIONS:
                if (action.payload && Array.isArray(action.payload)) {
                    // eslint-disable-next-line
                    draft.simulations = action.payload;
                }
                break;

            case SET_CONSUMER_LOAN_SITUATION_SELECTED_SIMULATION:
                if (typeof action.payload === 'number') {
                    draft.selectedSimulation = action.payload
                }
                break;
    
            case RESET_STORE:
                draft.currentStep = 0;
                draft.previousStep = 0;
                draft.lastStep = 0;
                break;

            default:
                break;
        }
    });

export default consumerLoanSituationReducer;
